import React, { useContext, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { TableRow } from '@material-ui/core';
import { UserContext } from '@/components/App';
import { UPDATE_CLIENT_BY_PK } from '@/graphql/mutations/clients';
import { buildCanonicalFullAddress } from '@/utilities/address';
import { isValidEmail } from '@/utilities/validateEmail';
import { Skipped } from '@/components/CorporateUsers/shippers/blocks';
import ExternalIdentifiers from '@/components/CorporateUsers/modals/AccountDetailsModal/ExternalIdentifiers';
import {
    Card,
    ContactEditModal,
    DataCell,
    DataTable,
    EditCell,
    Header,
    PageSurface,
    Section,
    StrongCell,
    SubCell,
} from '@/components/CorporateShipper/blocks';
import { EMPTY_VALUE } from '@/components/CorporateShipper/constants';
import { contactRowsFor } from '@/components/CorporateShipper/utils';
import EditFieldsModal from '@/components/CorporateShipper/modals/EditFieldsModal';
import { billingPatch, identityPatch, isWholeFleetSize } from './utils';

const IDENTITY_FIELDS = [
    { key: 'dot_number', label: 'DOT #' },
    { key: 'mc_num', label: 'MC #' },
    { key: 'business_ein', label: 'EIN #' },
    { key: 'fleet_size', label: '# of trucks' },
    {
        key: 'email',
        label: 'Email',
        disabled: true,
        helperText: 'This is the login email. Admins cannot change account emails at this time.',
    },
];

const BILLING_FIELDS = [
    { key: 'business_name', label: 'Name', required: true },
    { key: 'alias', label: 'Alias', helperText: 'The Alias is the name your clients will see.' },
    { key: 'billing_address', label: 'Address' },
    { key: 'billing_address_unit', label: 'Unit' },
    { key: 'billing_city', label: 'City' },
    { key: 'billing_state', label: 'State' },
    { key: 'billing_zip', label: 'Zip' },
    { key: 'billing_email', label: 'Email' },
];

// Each section edits the viewed carrier's `clients` row; on failure the modal stays open with the draft intact.
const useCarrierEditor = () => {
    const { client: carrier } = useContext(UserContext);
    const [editing, setEditing] = useState(null);
    const [saveError, setSaveError] = useState(null);
    const [updateClient, { loading: saving }] = useMutation(UPDATE_CLIENT_BY_PK);

    const save = async (update) => {
        setSaveError(null);
        try {
            await updateClient({ variables: { client_id: carrier.client_id, update } });
            setEditing(null);
        } catch (err) {
            Sentry.captureException(err);
            setSaveError("Couldn't save. Try again.");
        }
    };

    const closeEditor = () => {
        setSaveError(null);
        setEditing(null);
    };

    return { carrier, editing, setEditing, saveError, setSaveError, saving, save, closeEditor };
};

const CarrierIdentity = () => {
    const { carrier, editing, setEditing, saveError, setSaveError, saving, save, closeEditor } = useCarrierEditor();

    const saveIdentity = (draft) => {
        if (!isWholeFleetSize(draft.fleet_size)) {
            setSaveError('# of trucks must be a whole number.');
            return;
        }

        const update = identityPatch(draft);
        save(update);
    };

    return (
        <Section>
            <Header title="Carrier identity" />
            <DataTable columns={['DOT #', 'MC #', 'EIN #', 'Onward carrier code', 'Email', 'Market', '# of trucks']}>
                <TableRow>
                    <DataCell>{carrier.dot_number || EMPTY_VALUE}</DataCell>
                    <DataCell>{carrier.mc_num || EMPTY_VALUE}</DataCell>
                    <DataCell>{carrier.business_ein || EMPTY_VALUE}</DataCell>
                    <DataCell>
                        <Skipped />
                    </DataCell>
                    <DataCell>{carrier.email || EMPTY_VALUE}</DataCell>
                    <DataCell>
                        <Skipped />
                    </DataCell>
                    <DataCell>{carrier.fleet_size ?? EMPTY_VALUE}</DataCell>
                    <EditCell label="Edit carrier identity" onClick={() => setEditing(true)} />
                </TableRow>
            </DataTable>
            <EditFieldsModal
                open={!!editing}
                title="Carrier identity"
                fields={IDENTITY_FIELDS}
                values={carrier}
                saving={saving}
                error={saveError}
                onClose={closeEditor}
                onSave={saveIdentity}
            />
        </Section>
    );
};

const BillingInformation = () => {
    const { carrier, editing, setEditing, saveError, setSaveError, saving, save, closeEditor } = useCarrierEditor();

    const billingAddress = buildCanonicalFullAddress({
        street: carrier.billing_address,
        unit: carrier.billing_address_unit,
        city: carrier.billing_city,
        state: carrier.billing_state,
        zip: carrier.billing_zip,
    });

    const saveBilling = (draft) => {
        const update = billingPatch(draft);
        if (update.billing_email && !isValidEmail(update.billing_email)) {
            setSaveError('Enter a valid email.');
            return;
        }

        save(update);
    };

    return (
        <Section>
            <Header title="Billing information" />
            <DataTable columns={['Name', 'Billing code', 'Email', 'Terms', 'Payment setup']}>
                <TableRow>
                    <DataCell>
                        <StrongCell>{carrier.business_name || EMPTY_VALUE}</StrongCell>
                        {carrier.alias && <SubCell>Alias: {carrier.alias}</SubCell>}
                        {billingAddress && <SubCell>{billingAddress}</SubCell>}
                    </DataCell>
                    <DataCell>
                        <Skipped />
                    </DataCell>
                    <DataCell>{carrier.billing_email || EMPTY_VALUE}</DataCell>
                    <DataCell>
                        <Skipped />
                    </DataCell>
                    <DataCell>
                        <Skipped />
                    </DataCell>
                    <EditCell label="Edit billing information" onClick={() => setEditing(true)} />
                </TableRow>
            </DataTable>
            <EditFieldsModal
                open={!!editing}
                title="Billing information"
                fields={BILLING_FIELDS}
                values={carrier}
                saving={saving}
                error={saveError}
                onClose={closeEditor}
                onSave={saveBilling}
            />
        </Section>
    );
};

const KeyContacts = () => {
    const { carrier, editing, setEditing, saveError, setSaveError, saving, save, closeEditor } = useCarrierEditor();
    const contacts = useMemo(() => contactRowsFor(carrier), [carrier]);

    const saveContact = (update) => {
        const email = update[editing.emailField];
        if (email && !isValidEmail(email)) {
            setSaveError('Enter a valid email.');
            return;
        }

        save(update);
    };

    return (
        <Section>
            <Header title="Key contacts" />
            {/* No "Add contact": `clients` has exactly these three slots. */}
            <DataTable columns={['Name', 'Title', 'Email', 'Phone']}>
                {contacts.map((contact) => (
                    <TableRow key={contact.key}>
                        <DataCell>
                            <StrongCell>{contact.name || EMPTY_VALUE}</StrongCell>
                        </DataCell>
                        <DataCell>{contact.title}</DataCell>
                        <DataCell>{contact.email || EMPTY_VALUE}</DataCell>
                        <DataCell>{contact.phone || EMPTY_VALUE}</DataCell>
                        <EditCell label={`Edit ${contact.title}`} onClick={() => setEditing(contact)} />
                    </TableRow>
                ))}
            </DataTable>
            <ContactEditModal
                open={!!editing}
                slot={editing}
                saving={saving}
                error={saveError}
                onClose={closeEditor}
                onSave={saveContact}
            />
        </Section>
    );
};

const CompanyInfo = () => {
    const { client: carrier } = useContext(UserContext);
    if (!carrier) return null;

    return (
        <PageSurface>
            <CarrierIdentity />
            <BillingInformation />
            <Section>
                <Header title="Identifier Details" note="· your organization's ids for this carrier" />
                <Card>
                    <div
                        css={css`
                            padding: 20px;
                        `}
                    >
                        <ExternalIdentifiers clientId={carrier.client_id} promptWhenEmpty />
                    </div>
                </Card>
            </Section>
            <KeyContacts />
        </PageSurface>
    );
};

export default CompanyInfo;
